import React, { memo } from 'react'
import DedicatedLanesModal from 'transfix-form/forms/CarrierLoadPreferences/components/DedicatedLanesModal'
import ExcludedCommoditiesModal from 'transfix-form/forms/CarrierLoadPreferences/components/ExcludedCommoditiesModal'
import HeavyLoadsModal from 'transfix-form/forms/CarrierLoadPreferences/components/HeavyLoadsModal'
import LoadTypesModal from 'transfix-form/forms/CarrierLoadPreferences/components/LoadTypesModal'
import RateTypesModal from 'transfix-form/forms/CarrierLoadPreferences/components/RateTypesModal'
import {
  getDedicatedLanesDefaultValues,
  getDedicatedLanesLabels,
  getExcludedCommoditiesDefaultValues,
  getExcludedCommoditiesLabels,
  getHeavyLoadsDefaultValues,
  getHeavyLoadsLabel,
  getLoadTypesDefaultValues,
  getLoadTypesLabels,
  getRateTypesDefaultValues,
  getRateTypesLabels,
} from 'transfix-form/forms/CarrierLoadPreferences/utils'
import Card from 'transfix-ui/components/Card'
import { Grid } from 'transfix-ui/core'
import Typography from 'transfix-ui/core/Typography'
import { useModalState } from 'utils/hooks/useModalState'
import SidebarCardRow from '../SidebarCardRow'
import { ICarrierProfileLoadPreferencesFragment } from './graphql/CarrierProfileLoadPreferencesFragment'
import {
  useDedicatedLanesSubmitter,
  useExcludedCommodotiesSubmitter,
  useHeavyLoadsSubmitter,
  useLoadTypesSubmitter,
  useRateTypesSubmitter,
} from './hooks'

export interface ILoadPreferencesCardProps {
  carrierApplicationId: string
  loadPreferences: Partial<ICarrierProfileLoadPreferencesFragment>
}

const LoadPreferencesCard = memo<ILoadPreferencesCardProps>(
  ({
    carrierApplicationId,
    loadPreferences: {
      dedicatedLaneRatePreference,
      excludedCommodities = [],
      loadingCapability,
      rateTypePreference,
    },
  }) => {
    const { createToggleHandler, modalState } = useModalState({
      loadTypesModal: false,
      rateTypesModal: false,
      dedicatedLanesModal: false,
      heavyLoadsModal: false,
      excludedCommoditiesModal: false,
    })

    const {
      dedicatedLanesModal: isDedicatedLanesModalOpen,
      excludedCommoditiesModal: isExcludedCommoditiesModalOpen,
      heavyLoadsModal: isHeavyLoadsModalOpen,
      loadTypesModal: isLoadTypesModalOpen,
      rateTypesModal: isRateTypesModalOpen,
    } = modalState || {}

    const toggleLoadTypesModalHandler = createToggleHandler('loadTypesModal')
    const toggleRateTypesModalHandler = createToggleHandler('rateTypesModal')
    const toggleDedicatedLanesModalHandler = createToggleHandler('dedicatedLanesModal')
    const toggleHeavyLoadsModalHandler = createToggleHandler('heavyLoadsModal')
    const toggleExcludedCommoditiesModalHandler = createToggleHandler('excludedCommoditiesModal')

    /**
     * Load Types
     */
    const {
      loading: loadTypesLoading,
      submitHandler: loadTypesSubmitHandler,
    } = useLoadTypesSubmitter(carrierApplicationId, loadingCapability?.id)
    const loadTypesLabel = getLoadTypesLabels(loadingCapability)
    const loadTypesDefaultValues = getLoadTypesDefaultValues(loadingCapability)

    /**
     * Rate Types
     */
    const {
      loading: rateTypesLoading,
      submitHandler: rateTypesSubmitHandler,
    } = useRateTypesSubmitter(carrierApplicationId, rateTypePreference?.id)
    const rateTypesLabel = getRateTypesLabels(rateTypePreference)
    const rateTypesDefaultValues = getRateTypesDefaultValues(rateTypePreference)

    /**
     * Dedicated Lanes
     */
    const {
      loading: dedicatedLanesLoading,
      submitHandler: dedicatedLanesSubmitHandler,
    } = useDedicatedLanesSubmitter(carrierApplicationId, dedicatedLaneRatePreference?.id)
    const {
      preferenceLabel: dedicatedLanesPreferenceLabel,
      rpmLabel: dedicatedLanesRpmLabel,
    } = getDedicatedLanesLabels(dedicatedLaneRatePreference)
    const dedicatedLanesDefaultValues = getDedicatedLanesDefaultValues(dedicatedLaneRatePreference)

    /**
     * Heavy Loads
     */
    const {
      loading: heavyLoadsLoading,
      submitHandler: heavyLoadsSubmitHandler,
    } = useHeavyLoadsSubmitter(carrierApplicationId, loadingCapability?.id)
    const heavyLoadsLabel = getHeavyLoadsLabel(loadingCapability)
    const heavyLoadsDefaultValues = getHeavyLoadsDefaultValues(loadingCapability)

    /**
     * Excluded Commodities
     */
    const {
      loading: excludedCommodotiesLoading,
      submitHandler: excludedCommoditiesSubmitHandler,
    } = useExcludedCommodotiesSubmitter(carrierApplicationId)
    const excludedCommoditiesLabel = getExcludedCommoditiesLabels(excludedCommodities)
    const excludedCommoditiesDefaultValues = getExcludedCommoditiesDefaultValues(
      excludedCommodities
    )

    return (
      <Card>
        <Typography variant='h3'>Load Preferences</Typography>
        <Grid container direction='column'>
          <SidebarCardRow
            title='Load types'
            value={loadTypesLabel}
            onEdit={toggleLoadTypesModalHandler}
          />
          <SidebarCardRow
            title='Rate types'
            value={rateTypesLabel}
            onEdit={toggleRateTypesModalHandler}
          />
          <SidebarCardRow
            title='Looking for dedicated lanes?'
            value={
              <>
                <Typography component='div' variant='body1'>
                  {dedicatedLanesPreferenceLabel}
                </Typography>
                {dedicatedLanesRpmLabel && (
                  <Typography component='div' variant='body1' color='textSecondary'>
                    {dedicatedLanesRpmLabel}
                  </Typography>
                )}
              </>
            }
            onEdit={toggleDedicatedLanesModalHandler}
          />
          <SidebarCardRow
            title='Run heavy loads (over 42k lbs)'
            value={heavyLoadsLabel}
            onEdit={toggleHeavyLoadsModalHandler}
          />
          <SidebarCardRow
            title='Excluded commodities'
            value={excludedCommoditiesLabel}
            onEdit={toggleExcludedCommoditiesModalHandler}
          />
        </Grid>

        {isLoadTypesModalOpen && (
          <LoadTypesModal
            defaultValues={loadTypesDefaultValues}
            loading={loadTypesLoading}
            onClose={toggleLoadTypesModalHandler}
            onSubmit={loadTypesSubmitHandler}
          />
        )}
        {isRateTypesModalOpen && (
          <RateTypesModal
            defaultValues={rateTypesDefaultValues}
            loading={rateTypesLoading}
            onClose={toggleRateTypesModalHandler}
            onSubmit={rateTypesSubmitHandler}
          />
        )}
        {isDedicatedLanesModalOpen && (
          <DedicatedLanesModal
            defaultValues={dedicatedLanesDefaultValues}
            loading={dedicatedLanesLoading}
            onClose={toggleDedicatedLanesModalHandler}
            onSubmit={dedicatedLanesSubmitHandler}
          />
        )}
        {isExcludedCommoditiesModalOpen && (
          <ExcludedCommoditiesModal
            defaultValues={excludedCommoditiesDefaultValues}
            loading={excludedCommodotiesLoading}
            onClose={toggleExcludedCommoditiesModalHandler}
            onSubmit={excludedCommoditiesSubmitHandler}
          />
        )}
        {isHeavyLoadsModalOpen && (
          <HeavyLoadsModal
            defaultValues={heavyLoadsDefaultValues}
            loading={heavyLoadsLoading}
            onClose={toggleHeavyLoadsModalHandler}
            onSubmit={heavyLoadsSubmitHandler}
          />
        )}
      </Card>
    )
  }
)

export default LoadPreferencesCard
